<script setup>
/* global axios */
import { computed, onUnmounted, ref } from "vue";
import { useToggle } from "@vueuse/core";
import { useStore } from "vuex";
import { useAlert } from "dashboard/composables";
import { useI18n } from "vue-i18n";
import { emitter } from "shared/helpers/mitt";
import EmailTranscriptModal from "./EmailTranscriptModal.vue";
import ResolveAction from "../../buttons/ResolveAction.vue";
import ButtonV4 from "dashboard/components-next/button/Button.vue";
import DropdownMenu from "dashboard/components-next/dropdown-menu/DropdownMenu.vue";

import {
  CMD_MUTE_CONVERSATION,
  CMD_SEND_TRANSCRIPT,
  CMD_UNMUTE_CONVERSATION,
} from "dashboard/helper/commandbar/events";

const store = useStore();
const { t } = useI18n();

const [showEmailActionsModal, toggleEmailModal] = useToggle(false);
const [showActionsDropdown, toggleDropdown] = useToggle(false);

const historyImporting = ref(false);

const currentChat = computed(() => store.getters.getSelectedChat);
const accountId = computed(() => store.getters.getCurrentAccountId);

const isUnipileConversation = computed(() => {
  const chat = currentChat.value || {};
  const inboxId = Number(chat.inbox_id || chat.inboxId);

  return inboxId === 10 || inboxId === 11;
});

const conversationApiUrl = computed(() => {
  const conversationId = currentChat.value?.id;

  if (!accountId.value || !conversationId) return null;

  return `/api/v1/accounts/${accountId.value}/conversations/${conversationId}`;
});

const actionMenuItems = computed(() => {
  const items = [];

  if (!currentChat.value.muted) {
    items.push({
      icon: "i-lucide-volume-off",
      label: t("CONTACT_PANEL.MUTE_CONTACT"),
      action: "mute",
      value: "mute",
    });
  } else {
    items.push({
      icon: "i-lucide-volume-1",
      label: t("CONTACT_PANEL.UNMUTE_CONTACT"),
      action: "unmute",
      value: "unmute",
    });
  }

  items.push({
    icon: "i-lucide-share",
    label: t("CONTACT_PANEL.SEND_TRANSCRIPT"),
    action: "send_transcript",
    value: "send_transcript",
  });

  if (isUnipileConversation.value) {
    items.push({
      icon: "i-lucide-history",
      label: historyImporting.value
        ? "Importando historial…"
        : "Traer 25 mensajes anteriores",
      action: "import_history",
      value: "import_history",
      disabled: historyImporting.value,
    });
  }

  return items;
});

const importUnipileHistory = async () => {
  if (historyImporting.value) return;

  const baseUrl = conversationApiUrl.value;

  if (!baseUrl) {
    window.alert("No se pudo identificar la conversación.");
    return;
  }

  historyImporting.value = true;

  try {
    const confirmed = window.confirm(
      "¿Quieres traer todo el historial disponible de esta conversación? Se hará por bloques en segundo plano y podrás seguir trabajando."
    );

    if (!confirmed) return;

    await axios.post(`${baseUrl}/import_unipile_history`);

    window.alert(
      "La importación completa se ha iniciado en segundo plano. Incluye texto, fotografías, audios y los demás archivos que Unipile tenga disponibles."
    );
    window.location.reload();
  } catch (error) {
    console.error("Error importando historial Unipile", error);

    const detail =
      error?.response?.data?.error ||
      error?.response?.data?.message ||
      error?.message ||
      "Error desconocido";

    window.alert(
      `No se pudo importar el historial: ${detail}. Código: ${
        error?.response?.status || "desconocido"
      }`
    );
  } finally {
    historyImporting.value = false;
  }
};

const handleActionClick = async ({ action }) => {
  toggleDropdown(false);

  if (action === "import_history") {
    await importUnipileHistory();
  } else if (action === "mute") {
    store.dispatch("muteConversation", currentChat.value.id);
    useAlert(t("CONTACT_PANEL.MUTED_SUCCESS"));
  } else if (action === "unmute") {
    store.dispatch("unmuteConversation", currentChat.value.id);
    useAlert(t("CONTACT_PANEL.UNMUTED_SUCCESS"));
  } else if (action === "send_transcript") {
    toggleEmailModal();
  }
};

const mute = () => {
  store.dispatch("muteConversation", currentChat.value.id);
  useAlert(t("CONTACT_PANEL.MUTED_SUCCESS"));
};

const unmute = () => {
  store.dispatch("unmuteConversation", currentChat.value.id);
  useAlert(t("CONTACT_PANEL.UNMUTED_SUCCESS"));
};

emitter.on(CMD_MUTE_CONVERSATION, mute);
emitter.on(CMD_UNMUTE_CONVERSATION, unmute);
emitter.on(CMD_SEND_TRANSCRIPT, toggleEmailModal);

onUnmounted(() => {
  emitter.off(CMD_MUTE_CONVERSATION, mute);
  emitter.off(CMD_UNMUTE_CONVERSATION, unmute);
  emitter.off(CMD_SEND_TRANSCRIPT, toggleEmailModal);
});
</script>

<template>
  <div class="relative flex items-center gap-2 actions--container">
    <ResolveAction
      :conversation-id="currentChat.id"
      :status="currentChat.status"
    />

    <div
      v-on-clickaway="() => toggleDropdown(false)"
      class="relative flex items-center group"
    >
      <ButtonV4
        v-tooltip="$t('CONVERSATION.HEADER.MORE_ACTIONS')"
        size="sm"
        variant="ghost"
        color="slate"
        icon="i-lucide-more-vertical"
        class="rounded-md group-hover:bg-n-alpha-2"
        @click="toggleDropdown()"
      />

      <DropdownMenu
        v-if="showActionsDropdown"
        :menu-items="actionMenuItems"
        class="mt-1 ltr:right-0 rtl:left-0 top-full"
        @action="handleActionClick"
      />
    </div>

    <EmailTranscriptModal
      v-if="showEmailActionsModal"
      :show="showEmailActionsModal"
      :current-chat="currentChat"
      @cancel="toggleEmailModal"
    />
  </div>
</template>
